import { Box, Toolbar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH, readSidebarCollapsed, writeSidebarCollapsed } from './sidebarState';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth.service';
import { useErrorStore } from '@/stores/errorStore';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const storeLogout = useAuthStore((s) => s.logout);
  const showError = useErrorStore((s) => s.showError);

  // ADR-046 / QA #30: idle for 30 minutes → sign out with a clear message.
  const { warning, stay } = useIdleTimer(isAuthenticated, () => {
    authService.logout().catch(() => undefined);
    storeLogout();
    showError('閒置超過 30 分鐘，已自動登出', 'warning');
    navigate('/login');
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Desktop sidebar collapsed to its icon rail; remembered across reloads.
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const handleCollapseToggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeSidebarCollapsed(next);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Header onMenuClick={handleDrawerToggle} />
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={handleDrawerToggle}
        collapsed={collapsed}
        onToggleCollapsed={handleCollapseToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // Follows the drawer's actual width (this said 240px against a 280px drawer).
          width: { sm: `calc(100% - ${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px)` },
          height: '100vh',
          overflowY: 'scroll',  // 始終顯示垂直滾動條，避免重排問題
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      <Dialog open={warning} onClose={stay}>
        <DialogTitle>即將自動登出</DialogTitle>
        <DialogContent>
          <Typography>您已閒置將近 30 分鐘，一分鐘後將自動登出。要繼續使用嗎？</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={stay} autoFocus>
            繼續使用
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
